import {
  compact,
  get,
  isArray,
  isDate,
  isNaN,
  isNumber,
  isString,
  isUndefined,
  join,
  map,
  omit,
  reduce,
  size,
  split,
  tail,
} from "lodash";
import QueryString from "query-string";
import isURL from "validator/lib/isURL";
import { isDateMath, isMoment, mmt } from "@tootools/date";

function isNumeric(x: any) {
  if (isNumber(x)) {
    return true;
  } else if (isString(x)) {
    return x !== "" ? !isNaN(Number(x)) : false;
  } else {
    return false;
  }
}

const stringifyDate = (date: any) => {
  return mmt(date).toISOString(false);
};

const getStringifiedParam = (
  values: string | object | any[]
): string | null => {
  if (isString(values) && isDateMath(values)) {
    return values;
  } else if (isDate(values) || isMoment(values)) {
    return stringifyDate(values);
  } else if (Array.isArray(values)) {
    const _values = join(values, ",");
    return _values === "" ? null : _values;
  } else if (typeof values === "object") {
    const _values = join(
      map(values, (value, key) => {
        return (
          key +
          ":" +
          join(
            map(!isArray(value) ? [value] : value, (v) => {
              if (isString(v) && isDateMath(v)) {
                return v;
              } else if (isDate(v) || isMoment(v)) {
                return stringifyDate(v);
              } else {
                return v;
              }
            }),
            ";"
          )
        );
      }),
      ","
    );
    return _values === "" ? null : _values;
  } else {
    return values === "" ? null : values;
  }
};

const formatSearchParam = (values: any): any => {
  if (values && !isUndefined(values)) {
    if (values.indexOf(":") === -1) {
      return values.indexOf(",") === -1 ? values : values.split(",");
    } else {
      return reduce(
        values.split(","),
        (acc, entry) => {
          const split = entry.split(":");
          if (split[1]) {
            if (split[1].indexOf(";") !== -1) {
              acc[split[0]] = split[1].split(";");
            } else {
              acc[split[0]] = join(tail(split), ":");
            }
          }
          return acc;
        },
        {} as any
      );
    }
  } else {
    return "";
  }
};

const getFormatedValue = (value: any): string => {
  if (isString(value) && isDateMath(value)) {
    return value;
  } else if (isDate(value) || isMoment(value)) {
    return stringifyDate(value);
  } else if (isString(value) || isNumber(value)) {
    return "" + value;
  } else if (isArray(value)) {
    return "[" + value + "]";
  } else {
    return get(value, "id", "");
  }
};

export function stringifySearchParams(
  searchParams: Record<string, any>,
  encode: boolean = true
): string {
  const params = reduce(
    searchParams,
    (acc, values, key: string) => {
      return {
        ...acc,
        [key]: getStringifiedParam(values),
      };
    },
    {}
  );
  return QueryString.stringify(params, { encode });
}

function parseParamValue(value: any): any {
  if (isNumeric(value)) {
    return parseInt(value);
  } else if (isArray(value)) {
    return map(value, parseParamValue);
  } else if (isString(value)) {
    if (value.indexOf("[") === 0 && value.indexOf("]") === value.length - 1) {
      return parseParamValue(
        compact(split(value.substring(1, value.length - 1), ","))
      );
    } else if (value.indexOf("@") === 0) {
      // @DEPRECATED
      return new Date(parseInt(value.substr(1)));
    } else {
      return value;
    }
  } else if (typeof value === "object") {
    //  TODO  why ?
    return reduce(
      value,
      (acc, value, key: number) => {
        return {
          ...acc,
          [key]: parseParamValue(value),
        };
      },
      {}
    );
  } else {
    return value;
  }
}

export function parseSearchParams(search: string): { [key: string]: any } {
  return reduce(
    QueryString.parse(search),
    (params, value, key: string) => {
      return {
        ...params,
        [key]: parseParamValue(formatSearchParam(value)),
      };
    },
    {}
  );
}

export function getUrl(
  pathname: string,
  params: Record<string, any>,
  encode: boolean = true
): string {
  const replacedTokens: string[] = [];
  const processedPathName = reduce(
    params,
    (acc, value, token: string) => {
      const key = ":" + token;
      if (acc.indexOf(key) !== -1) {
        replacedTokens.push(token);
        return acc.replace(key, getFormatedValue(value));
      } else {
        return acc;
      }
    },
    pathname
  );
  const searchParams = omit(params, replacedTokens);
  if (size(searchParams)) {
    return (
      processedPathName + "?" + stringifySearchParams(searchParams, encode)
    );
  } else {
    return processedPathName;
  }
}

export function cleanUrl(url: string): string {
  url = "" + (url || "");
  if (url.search(/^http[s]?:\/\//) === -1) {
    url = "http://" + url;
  }
  //replace /reel/ by /p/ for instagram urls
  return url.replace(/\/$/, "").replace("/reel/", "/p/");
}

export function cleanLabel(url: string): string {
  return ("" + (url || "")).replace(/.*?:\/\//g, "");
}

export function isValid(url: string): boolean {
  const regex = new RegExp(
    "^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?"
  );
  return regex.test("" + (url || ""));
}

export function getWindowLocationParam(param: string): string {
  const results = new RegExp("[?&]" + param + "=([^&#]*)").exec(
    window.location.href
  );
  if (!results) return "";
  return decodeURI(results[1]);
}

export const ping = (url: string): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    if (!isURL(url)) reject();
    try {
      fetch(`https://cors-anywhere.herokuapp.com/${url}`)
        .then((_) => resolve(true))
        .catch((_) => reject());
    } catch (_) {
      reject();
    }
  });
};

export const getUrlParamsAsObject = <T extends string[]>(
  params: T
): Record<string, string | null> => {
  const urlParams = new URLSearchParams(window.location.hash.split("?")[1]);
  return reduce(
    params,
    (acc, param) => {
      if (urlParams.has(param)) {
        acc[param] = urlParams.get(param);
      }
      return acc;
    },
    {} as Record<string, string | null>
  );
};
